import {
  TARGET_CHANNEL_COUNT as PCM_CHANNELS,
  PCM_FRAME_DURATION_MS,
  PCM_FRAME_SAMPLES,
  TARGET_SAMPLE_RATE as PCM_SAMPLE_RATE,
} from "./audio-config";
import type { RawChunk } from "./audio-chunker";
import type { AudioFrame } from "./pcm-framer";

export {
  PCM_CHANNELS,
  PCM_FRAME_DURATION_MS,
  PCM_FRAME_SAMPLES,
  PCM_SAMPLE_RATE,
};
export type { AudioFrame, RawChunk };

/**
 * Exact LongformAudioProxy `bringup-test-server` WebSocket contract. Keeping
 * the JSON shape here prevents transport/session lifecycle code from guessing
 * at server field names or error semantics.
 */

export interface StartSessionInput {
  apiKey: string;
  /** Omit for a new proxy session; include only when resuming a server ID. */
  proxySessionId?: string;
  sampleRate?: number;
}

export interface AudioMessageInput {
  sequenceNumber: number;
  /** Base64-encoded raw mono Float32 LE PCM. */
  frames: string;
}

export interface ProxySessionStartedMessage {
  type: "session_started";
  session_id: string;
  resumed: boolean;
  server_time: string;
  sample_rate: number;
  total_bytes: number;
}

export interface ProxyAckMessage {
  type: "ack";
  seq: number;
  total_bytes: number;
}

export interface ProxySessionEndedMessage {
  type: "session_ended";
  session_id: string;
  total_bytes: number;
  chunk_count: number;
}

export const LONGFORM_PROXY_ERROR_CODES = [
  "unauthorized",
  "malformed_message",
  "protocol_violation",
  "unknown_session",
  "session_in_use",
  "sample_rate_mismatch",
  "bad_audio_frame",
  "backpressure",
  "internal_error",
] as const;

export type LongformProxyErrorCode =
  (typeof LONGFORM_PROXY_ERROR_CODES)[number];

export interface ProxyErrorMessage {
  type: "error";
  code: LongformProxyErrorCode;
  message: string;
}

export type LongformProxyServerMessage =
  | ProxySessionStartedMessage
  | ProxyAckMessage
  | ProxySessionEndedMessage
  | ProxyErrorMessage;

export interface AudioProxyProtocol {
  createStartSessionMessage(input: StartSessionInput): string;
  createAudioMessage(input: AudioMessageInput): string;
  createEndSessionMessage(): string;
  parseServerMessage(message: unknown): LongformProxyServerMessage | undefined;
}

export const longformAudioProxyProtocol: AudioProxyProtocol = {
  createStartSessionMessage({
    apiKey,
    proxySessionId,
    sampleRate = PCM_SAMPLE_RATE,
  }): string {
    return JSON.stringify({
      type: "start_session",
      key: apiKey,
      sample_rate: sampleRate,
      ...(proxySessionId ? { session_id: proxySessionId } : {}),
    });
  },

  createAudioMessage({ sequenceNumber, frames }): string {
    return JSON.stringify({
      type: "audio",
      seq: sequenceNumber,
      frames,
    });
  },

  createEndSessionMessage(): string {
    return JSON.stringify({ type: "end_session" });
  },

  parseServerMessage(message: unknown): LongformProxyServerMessage | undefined {
    const parsed = parseJsonObject(message);
    if (!parsed || typeof parsed.type !== "string") return undefined;

    switch (parsed.type) {
      case "session_started":
        return isSessionStartedMessage(parsed)
          ? (parsed as unknown as ProxySessionStartedMessage)
          : undefined;
      case "ack":
        return isAckMessage(parsed)
          ? (parsed as unknown as ProxyAckMessage)
          : undefined;
      case "session_ended":
        return isSessionEndedMessage(parsed)
          ? (parsed as unknown as ProxySessionEndedMessage)
          : undefined;
      case "error":
        return isErrorMessage(parsed)
          ? (parsed as unknown as ProxyErrorMessage)
          : undefined;
      default:
        return undefined;
    }
  },
};

/** Backwards-compatible export name while call sites migrate. */
export const provisionalAudioProxyProtocol = longformAudioProxyProtocol;

export function isPermanentProxyError(code: LongformProxyErrorCode): boolean {
  return [
    "unauthorized",
    "protocol_violation",
    "unknown_session",
    "session_in_use",
    "sample_rate_mismatch",
  ].includes(code);
}

export function parseJsonObject(
  message: unknown,
): Record<string, unknown> | undefined {
  if (typeof message !== "string") return undefined;
  try {
    const parsed: unknown = JSON.parse(message);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function isSessionStartedMessage(
  message: Record<string, unknown>,
): boolean {
  return (
    typeof message.session_id === "string" &&
    typeof message.resumed === "boolean" &&
    typeof message.server_time === "string" &&
    typeof message.sample_rate === "number" &&
    typeof message.total_bytes === "number"
  );
}

function isAckMessage(
  message: Record<string, unknown>,
): boolean {
  return typeof message.seq === "number" && typeof message.total_bytes === "number";
}

function isSessionEndedMessage(
  message: Record<string, unknown>,
): boolean {
  return (
    typeof message.session_id === "string" &&
    typeof message.total_bytes === "number" &&
    typeof message.chunk_count === "number"
  );
}

function isErrorMessage(
  message: Record<string, unknown>,
): boolean {
  return (
    typeof message.code === "string" &&
    LONGFORM_PROXY_ERROR_CODES.includes(
      message.code as LongformProxyErrorCode,
    ) &&
    typeof message.message === "string"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
